import {
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  Switch,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useCallback, useState } from "react";
import { emptyCellDash } from "src/components";
import { baseDownloadUrl } from "src/context";
import { FinishSchedule_DesignPackageFragment } from "src/routes/libraries/design-catalog/finish-schedules/FinishSchedule.generated";
import { openInSelf } from "src/utils/window";
import { FinishScheduleFilters } from "../FinishSchedulePage";

type ExportFinishScheduleModalProps = {
  designPackage: FinishSchedule_DesignPackageFragment;
  exportFilter: FinishScheduleFilters | undefined;
};

export function ExportFinishScheduleModal(props: ExportFinishScheduleModalProps) {
  const { designPackage, exportFilter } = props;
  const [visualMode, setVisualMode] = useState(false);
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const { version, aggregateVersions, combinations } = designPackage;
  const [changesSinceRpavId, setChangesSinceRpavId] = useState<string | undefined>(undefined);
  const rpavOptions = aggregateVersions
    // TODO: Fix the GQL schema if RPAV.version is not supposed to be nullable
    .filter((v) => v.version! < version.version!)
    .reverse()
    .map((v) => ({
      id: v.id,
      // Non-latest RPAVs always have a publishedAt
      name: `v${v.version} - ${v.publishedAt!.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`,
    }));
  const [selectedCombinations, setSelectedCombinations] = useState<string[]>(
    initializeSelectedCombinations(designPackage, exportFilter),
  );

  const onExport = useCallback(() => {
    openInSelf(
      `${baseDownloadUrl()}/${visualMode ? "pdf" : "xlsx"}?${buildQueryParams(
        visualMode,
        designPackage.id,
        designPackage.version.id,
        selectedCombinations,
        changesSinceRpavId,
      )}`,
    );
    triggerNotice({
      icon: "info",
      message:
        "Generating document... This may take a few moments to complete. Please don't close this tab until the process is finished.",
    });
    closeModal();
  }, [visualMode, designPackage, selectedCombinations, changesSinceRpavId]);

  return (
    <>
      <ModalHeader>Export {designPackage.name} Finish Schedule</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.my1.gap2.$}>
          <Switch
            label="Visual Mode"
            selected={visualMode}
            onChange={(checked) => setVisualMode(checked)}
            tooltip="Visual mode exports a PDF of the finish schedule with the product selections highlighted."
          />
          {!visualMode && (
            <SelectField
              label="Changes Since"
              unsetLabel={emptyCellDash}
              options={rpavOptions}
              value={changesSinceRpavId}
              onSelect={(id) => setChangesSinceRpavId(id)}
              autoSort={false}
            />
          )}
          <div css={Css.df.fdc.gap1.$}>
            <label>Interior Scheme Spec Level Pairings</label>
            {combinations.map((combo) => (
              <Checkbox
                key={combo.id}
                label={combo.name}
                onChange={(selected) => {
                  if (selected) {
                    setSelectedCombinations([...selectedCombinations, combo.id]);
                  } else {
                    setSelectedCombinations(selectedCombinations.filter((id) => id !== combo.id));
                  }
                }}
                selected={selectedCombinations.includes(combo.id)}
              />
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div css={Css.df.gap2.$}>
          <Button variant="quaternary" label="Cancel" onClick={closeModal} />
          <Button variant="primary" label="Export" onClick={onExport} />
        </div>
      </ModalFooter>
    </>
  );
}

function buildQueryParams(
  visualMode: boolean,
  designPackageId: string,
  designRpavId: string,
  combinations: string[],
  changesSinceRpavId: string | undefined,
) {
  // build the query params for the document download url
  const params = new URLSearchParams({
    designRpavId,
    type: visualMode ? "designPackageFinishScheduleVisualMode" : "finishSchedule",
    designPackageId,
    ...(!visualMode && changesSinceRpavId && { changesSinceRpavId }),
  });
  combinations.forEach((combo) => params.append("combinations", combo));
  return params.toString();
}

// If any forDesignPackage filters have been applied, initialize the selectedCombinations with the combinations that match the filters
function initializeSelectedCombinations(
  designPackage: FinishSchedule_DesignPackageFragment,
  filter: FinishScheduleFilters | undefined,
) {
  const { options, combinations } = designPackage;
  // Determine if there have been any forDesignPackage filters applied
  const designPackageOptionGroups = options
    .filter((rpo) => rpo.optionGroup.forDesignPackages)
    .map((rpo) => rpo.optionGroup.name)
    .unique();
  const filteredDesignPackageOptionGroups = designPackageOptionGroups
    .map((name) => filter?.[name] ?? [])
    .filter((og) => og.nonEmpty);
  if (filteredDesignPackageOptionGroups.isEmpty) return [];
  // If there have been check if the combination has all of the filtered option groups
  return combinations
    .filter((comb) =>
      filteredDesignPackageOptionGroups.every((fdpog) => comb.options.some((rpo) => fdpog.includes(rpo.id))),
    )
    .map((comb) => comb.id);
}
